/**
 * **房主侧的远程机器人传输。**
 *
 * 把「轮到某个 bot 座位」的局面推给一个跑在 Node 里的 WebSocket 服务端
 * （独立的 MCP 进程，见 mcp/），并等它回一个**合法动作序号**。
 *
 * 三条设计约束：
 * - 浏览器只能主动拨出，所以这里永远是 WebSocket **客户端**；MCP 聊天机器人
 *   本身不是一个游戏 peer，它只是房主代打的另一条"大脑"。
 * - 序号由服务端在房主发过去的 `options` 数组上选，房主再用**本地重新枚举的**
 *   `options` 映射回动作。LLM 不能绕过 `reduce`。
 * - 没连上 / 超时 / 序号越界，一律返回 `null`，房主回退到本地启发式
 *   `chooseBotAction`，绝不让整桌卡死。
 *
 * 本文件不 import 任何渲染 / UI 代码，纯 host 逻辑。
 */
import type { CardDef } from '../core/card';
import type { Option } from '../core/options';
import type { PlayerView } from '../core/project';
import { cardDescription, cardLore, cardName, cardNameResolver } from '../core/cardText';
import { loreToPlainText } from '../core/lore';

/** 卡牌背景文本：MCP 服务端没有内容包，得由房主随回合一起发过去 */
export interface RemoteCardLore {
  readonly name: string;
  readonly description: string;
  readonly lore: string;
}

/** 一次「该你动了」的完整快照 */
export interface RemoteTurn {
  /** 关联 decision 用的 id（同一座位同一时刻唯一即可） */
  readonly id: string;
  readonly seat: string;
  readonly seatName: string;
  /** 该座位视角的投影视图 */
  readonly view: PlayerView;
  /** 本地枚举出来的合法动作，服务端只需回下标 */
  readonly options: readonly Option[];
  /** 记牌器分析（src/analysis/tracker.ts 的 TrackerMemory） */
  readonly analysis: unknown;
  /** 牌名 → 背景文本 */
  readonly lore: Readonly<Record<string, RemoteCardLore>>;
}

/** 别人的行动触发的局面推送（"唤醒"用；不含隐藏信息） */
export interface RemoteSignal {
  readonly seat: string;
  readonly seatName: string;
  readonly view: PlayerView;
  readonly analysis: unknown;
  readonly lore: Readonly<Record<string, RemoteCardLore>>;
  /** core/events.ts 的 GameEvent[]，公开事件 */
  readonly events: readonly unknown[];
}

export interface RemoteBotTransport {
  /** 现在能不能立刻问到（没连上就别等，直接回退） */
  ready(): boolean;
  /** 问一次决策；返回 options 的下标，失败返回 null */
  decide(turn: RemoteTurn, timeoutMs: number): Promise<number | null>;
  /** 别人行动后的局面推送。火并忘；没连上就丢。 */
  signal?(signal: RemoteSignal): void;
}

export const DEFAULT_REMOTE_BOT_URL = 'ws://127.0.0.1:8790';

/**
 * 远程机器人是否启用、连到哪。
 *
 * 优先级：`globalThis.__ROULETTE_MCP_URL__` → URL 查询参数 `?mcp=<url|1>`。
 * `1` 表示用默认端口。没配就是 null —— 那么所有 bot 还是本地启发式。
 */
export function remoteBotUrl(): string | null {
  const configured = (globalThis as { __ROULETTE_MCP_URL__?: unknown }).__ROULETTE_MCP_URL__;
  if (typeof configured === 'string' && configured.length > 0) return configured;
  if (typeof location !== 'undefined' && typeof location.search === 'string') {
    const flag = new URLSearchParams(location.search).get('mcp');
    if (flag !== null && flag.length > 0) return flag === '1' ? DEFAULT_REMOTE_BOT_URL : flag;
  }
  return null;
}

type SocketFactory = (url: string) => WebSocket;

/** 用真实 WebSocket 的连接；断线自动重连 */
export class WebSocketRemoteBot implements RemoteBotTransport {
  readonly #url: string;
  readonly #makeSocket: SocketFactory;
  #socket: WebSocket | null = null;
  #reconnect: ReturnType<typeof setTimeout> | null = null;
  #pending = new Map<string, (index: number | null) => void>();
  #stopped = false;

  constructor(url: string = DEFAULT_REMOTE_BOT_URL, makeSocket: SocketFactory = (u) => new WebSocket(u)) {
    this.#url = url;
    this.#makeSocket = makeSocket;
  }

  ready(): boolean {
    return this.#socket !== null && this.#socket.readyState === 1;
  }

  decide(turn: RemoteTurn, timeoutMs: number): Promise<number | null> {
    this.#connect();
    const socket = this.#socket;
    if (socket === null || socket.readyState !== 1) return Promise.resolve(null);
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.#pending.delete(turn.id);
        resolve(null);
      }, timeoutMs);
      this.#pending.set(turn.id, (index) => {
        clearTimeout(timer);
        resolve(index);
      });
      socket.send(JSON.stringify({ t: 'turn', ...turn }));
    });
  }

  signal(payload: RemoteSignal): void {
    this.#connect();
    const socket = this.#socket;
    if (socket === null || socket.readyState !== 1) return;
    socket.send(JSON.stringify({ t: 'signal', ...payload }));
  }

  /** 主动断开（测试 / 退出房间） */
  stop(): void {
    this.#stopped = true;
    if (this.#reconnect !== null) clearTimeout(this.#reconnect);
    this.#reconnect = null;
    this.#failAll();
    try {
      this.#socket?.close();
    } catch {
      /* ignore */
    }
    this.#socket = null;
  }

  #connect(): void {
    if (this.#stopped) return;
    if (this.#socket !== null && (this.#socket.readyState === 0 || this.#socket.readyState === 1)) return;
    let socket: WebSocket;
    try {
      socket = this.#makeSocket(this.#url);
    } catch {
      this.#scheduleReconnect();
      return;
    }
    this.#socket = socket;
    socket.onopen = () => {
      socket.send(JSON.stringify({ t: 'hello', gameId: 'roulette' }));
    };
    socket.onmessage = (event: MessageEvent) => {
      this.#onMessage(event.data);
    };
    socket.onclose = () => {
      if (this.#socket === socket) this.#socket = null;
      this.#failAll();
      this.#scheduleReconnect();
    };
    socket.onerror = () => {
      /* 统一走 onclose */
    };
  }

  #scheduleReconnect(): void {
    if (this.#stopped || this.#reconnect !== null) return;
    this.#reconnect = setTimeout(() => {
      this.#reconnect = null;
      this.#connect();
    }, 2000);
  }

  #failAll(): void {
    for (const resolve of [...this.#pending.values()]) resolve(null);
    this.#pending.clear();
  }

  #onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let message: { t?: string; id?: string; index?: number };
    try {
      message = JSON.parse(raw) as { t?: string; id?: string; index?: number };
    } catch {
      return;
    }
    if (typeof message.id !== 'string') return;
    const resolve = this.#pending.get(message.id);
    if (resolve === undefined) return;
    this.#pending.delete(message.id);
    if (message.t === 'decision' && typeof message.index === 'number') resolve(message.index);
    else resolve(null);
  }
}

let shared: RemoteBotTransport | null = null;

/** 进程内单例：所有房间共用同一条到 MCP 的连接。没配 URL 时返回 null。 */
export function getRemoteBots(): RemoteBotTransport | null {
  if (shared !== null) return shared;
  const url = remoteBotUrl();
  if (url === null) return null;
  shared = new WebSocketRemoteBot(url);
  return shared;
}

/** 测试用：替换 / 清空单例 */
export function setRemoteBots(transport: RemoteBotTransport | null): void {
  shared = transport;
}

/** 从注册表里取所有牌面文本，随回合发给 MCP 服务端（它没有内容包） */
export function collectLore(cards: readonly CardDef[]): Record<string, RemoteCardLore> {
  const lore: Record<string, RemoteCardLore> = {};
  // lore 里的牌引用存的是 id，这里先把本批牌做成查表，解析成显示名再发出去
  const byId = new Map(cards.map((card) => [String(card.id), card]));
  const nameOf = cardNameResolver({ get: (id) => byId.get(String(id)) });
  for (const card of cards) {
    lore[String(card.id)] = {
      name: cardName(card),
      description: cardDescription(card),
      lore: loreToPlainText(cardLore(card), nameOf),
    };
  }
  return lore;
}
